import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, Loader2 } from 'lucide-react'
import { api, type SecurityDepositRecord } from '@/api/client'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { useToast } from '@/hooks/use-toast'
import { formatCurrency } from '@/lib/format-utils'
import {
  BUFFER_CHOICES_HOURS,
  DEFAULT_BUFFER_HOURS,
  followOnCandidates,
  holdUntilFor,
  shortDate,
} from '@/lib/deposit-hold'

interface ExtendDepositHoldDialogProps {
  reservationId: string
  deposit: SecurityDepositRecord
  open: boolean
  onOpenChange: (open: boolean) => void
}

const OTHER = 'other'

function formatInstant(iso: string | null): string {
  if (!iso) return ''
  const parsed = new Date(iso)
  if (Number.isNaN(parsed.getTime())) return ''
  return parsed.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

/**
 * Keep one booking's deposit hold live past its normal release (CAP-1286).
 *
 * For a stay that was extended outside the booking channel: the follow-on booking has no
 * card, so it can never carry a deposit of its own, and this booking's hold would run out
 * two days after the original checkout while the guest is still in the house.
 *
 * The dialog only records the instruction. Nothing is charged or held now: the hourly sweep
 * leaves Uplisting's hold alone while it is live, places our own on the same card once it
 * lapses, and renews it until the chosen time.
 */
export function ExtendDepositHoldDialog({
  reservationId,
  deposit,
  open,
  onOpenChange,
}: ExtendDepositHoldDialogProps) {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const propertyId = deposit.property_id ?? ''

  // The stay being extended, and the bookings that follow it, from the property's own list.
  const { data: stays, isLoading: staysLoading } = useQuery({
    queryKey: ['property-reservations', propertyId],
    queryFn: () => api.getPropertyReservations(propertyId),
    enabled: open && Boolean(propertyId),
    staleTime: 30000,
  })

  const thisStay = stays?.reservations.find(
    r => String(r.id ?? r.reservation_id ?? '') === reservationId
  )
  const candidates = followOnCandidates(
    stays?.reservations ?? [],
    reservationId,
    thisStay?.check_out
  )

  const [choice, setChoice] = useState<string | null>(null)
  const [otherDate, setOtherDate] = useState('')
  const [bufferHours, setBufferHours] = useState<number>(DEFAULT_BUFFER_HOURS)

  const candidateIds = candidates.map(c => String(c.id ?? c.reservation_id))
  const preferred = choice ?? deposit.hold_until_reservation_id ?? candidateIds[0] ?? OTHER
  // A booking that is no longer offered (cancelled since, or moved) must not leave the form with
  // nothing selected: fall back to the nearest follow-on, or to picking a date.
  const selected =
    preferred === OTHER || candidateIds.includes(preferred) ? preferred : (candidateIds[0] ?? OTHER)
  const followOn = candidates.find(c => String(c.id ?? c.reservation_id) === selected)
  const checkOutDate = selected === OTHER ? otherDate : (followOn?.check_out ?? null)
  const holdUntil = holdUntilFor(checkOutDate, bufferHours)

  const save = useMutation({
    mutationFn: () =>
      api.setDepositHoldUntil(reservationId, {
        hold_until: holdUntil,
        covers_reservation_id: selected === OTHER ? null : selected,
        reason: 'Extended stay, set from the reservation page',
      }),
    onSuccess: async result => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['reservation-security-deposit', reservationId],
        }),
        queryClient.invalidateQueries({ queryKey: ['protection-deposits'] }),
      ])
      toast({
        title: 'Hold extended',
        description: `Kept through ${formatInstant(result.hold_until)}. Nothing is charged.`,
      })
      onOpenChange(false)
    },
    onError: (err: unknown) =>
      toast({
        title: "Couldn't extend the hold",
        description: err instanceof Error ? err.message : 'Please try again.',
        variant: 'destructive',
      }),
  })

  const currentExpiry = formatInstant(deposit.authorization_expires_at)
  const heldByUplisting = deposit.status === 'uplisting_managed'
  const amount = formatCurrency(deposit.amount_cents / 100)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Extend this deposit hold</DialogTitle>
          <DialogDescription>
            Use this when the guest extended their stay outside the booking channel.
          </DialogDescription>
        </DialogHeader>

        <fieldset className="space-y-2">
          <legend className="mb-1 text-xs text-textMuted">Keep it for</legend>
          {staysLoading && <p className="text-sm text-textMuted">Looking for the next booking…</p>}
          {candidates.map(stay => {
            const id = String(stay.id ?? stay.reservation_id)
            return (
              <label
                key={id}
                className="flex cursor-pointer items-start gap-2 rounded-md border border-border p-2 text-sm has-[:checked]:border-mint"
              >
                <input
                  type="radio"
                  name="hold-for"
                  className="mt-1"
                  checked={selected === id}
                  onChange={() => setChoice(id)}
                />
                <span>
                  <span className="font-medium">
                    {stay.guest_name || `Booking ${id}`} · {stay.channel}
                  </span>
                  <br />
                  <span className="text-xs text-textMuted">
                    {shortDate(stay.check_in)} to {shortDate(stay.check_out)} · booking {id}
                  </span>
                </span>
              </label>
            )
          })}
          <label className="flex cursor-pointer items-center gap-2 rounded-md border border-border p-2 text-sm has-[:checked]:border-mint">
            <input
              type="radio"
              name="hold-for"
              checked={selected === OTHER}
              onChange={() => setChoice(OTHER)}
            />
            <span>Until a date I choose</span>
          </label>
          {selected === OTHER && (
            <div className="pl-6">
              <label className="mb-1 block text-xs text-textMuted" htmlFor="hold-other-date">
                Guest leaves on
              </label>
              <Input
                id="hold-other-date"
                type="date"
                value={otherDate}
                onChange={e => setOtherDate(e.target.value)}
              />
            </div>
          )}
        </fieldset>

        <div>
          <p className="mb-1 text-xs text-textMuted">Buffer after they leave</p>
          <div className="flex gap-2" role="group" aria-label="Buffer after checkout">
            {BUFFER_CHOICES_HOURS.map(hours => (
              <Button
                key={hours}
                type="button"
                size="sm"
                variant={bufferHours === hours ? 'default' : 'outline'}
                aria-pressed={bufferHours === hours}
                onClick={() => setBufferHours(hours)}
              >
                {hours} hours
              </Button>
            ))}
          </div>
        </div>

        <div className="rounded-md bg-surface p-3 text-xs leading-relaxed text-textMuted">
          {holdUntil ? (
            <>
              <p className="font-medium text-text">
                The hold will stay live through {formatInstant(holdUntil)}.
              </p>
              <p className="mt-1">
                {heldByUplisting
                  ? `Uplisting's hold lapses ${currentExpiry || 'soon'}. Within an hour after that, we place our own ${amount} hold on the same card`
                  : `We keep renewing the ${amount} hold on the same card`}
                {
                  ' and renew it about every 5 days. Nothing is charged, and it is released on its own after that time.'
                }
              </p>
            </>
          ) : (
            <p>
              {selected === OTHER ? 'Pick the date the guest leaves.' : 'Pick a booking above.'}
            </p>
          )}
        </div>

        <p className="flex items-start gap-2 text-xs text-warning">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span>
            This keeps {amount} pending on the guest&apos;s card for the extra days. Make sure they
            know.
          </span>
        </p>

        <DialogFooter className="gap-2">
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={!holdUntil || save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
            Extend hold
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
